// 디젠 상금 원장(degenPrizeLog) 대시보드 — 백페이 대사 도구 겸용.
// 조회·합계·state 대사 데이터는 전부 서버 RPC(adminDegenPrize.scanPrizeLog)가 계산하고
// 이 화면은 표시만 한다(클라 재계산 금지). verse 는 사이드바 활성 verse 를 그대로 탄다.
import { useState } from 'react';
import type {
  AdminDegenPrizeLogKind,
  AdminDegenPrizeScanResult,
} from '@rb/shared/admin-degen-prize.types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminScanDegenPrizeLog } from '@/client/degen-prize.client';
import { downloadCsv } from '@/lib/download';
import { isWiderPeriod, PERIOD_OPTIONS, type Period, periodStartMs } from '@/shop/revenue-period';

type Phase = 'idle' | 'loading' | 'done' | 'error';

const ALL_KINDS = 'all';
/** 'all' 기간의 하한 — degenPrizeLog 는 S2 배포 후에만 쌓이므로 이 이전 행은 없다. */
const PRIZE_LOG_EPOCH_MS = Date.parse('2026-08-01T00:00:00Z');

const KIND_LABEL: Record<AdminDegenPrizeLogKind, string> = {
  accrual: '적립',
  mini: '미니 당첨',
  mega: '메가 당첨',
  rescue: '구제 환급',
  golden_grant: '골든 티켓 지급',
  golden_use: '골든 티켓 사용',
};

/** 표에 뿌리는 행 수 상한 — 합계는 서버가 전체 기준으로 계산한다. */
const TABLE_ROW_LIMIT = 500;

/** 조회에 실제로 걸린 필터. 잔액 대사의 유효 조건을 판정하는 입력이다. */
export interface DegenPrizeScanFilters {
  period: Period;
  seasonId?: number;
  kind: string;
}

/**
 * 잔액 대사가 성립하는가 — 잭팟 state 는 **시즌과 무관한 전역·전기간 누계**다
 * (시즌 경계에서 리셋되지 않는다). 따라서 좌변(state)과 맞출 수 있는 우변은 필터가
 * 하나도 안 걸린 전기간 원장 합계뿐이다. 시즌·종류·기간 중 하나라도 좁히면 우변이
 * 부분합이 되어 차이값이 언제나 0 이 아니게 되고, 정상 상태가 빨간 글씨로 뜬다.
 */
export function isDegenPrizeReconcilable(filters: DegenPrizeScanFilters | null): boolean {
  if (!filters) return false;
  return filters.period === 'all' && filters.seasonId === undefined && filters.kind === ALL_KINDS;
}

export default function DegenPrizeLogPage() {
  const [period, setPeriod] = useState<Period>('7d');
  const [kind, setKind] = useState<string>(ALL_KINDS);
  const [seasonIdText, setSeasonIdText] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const [result, setResult] = useState<AdminDegenPrizeScanResult | null>(null);
  const [loadedPeriod, setLoadedPeriod] = useState<Period | null>(null);
  /**
   * 이번 조회에 실제로 걸린 필터. 잔액 대사의 유효 조건을 판정하는 데 쓴다 —
   * state 는 전역·전기간 누계인데 합계는 조회 창의 부분합이라, 필터가 하나라도
   * 걸리면 차이값이 항상 0 이 아니게 되어 "불일치"로 오독된다.
   */
  const [loadedFilters, setLoadedFilters] = useState<DegenPrizeScanFilters | null>(null);

  async function handleLoad() {
    setPhase('loading');
    setError('');
    const fetchedPeriod = period;
    try {
      const fromMs = periodStartMs(period) ?? PRIZE_LOG_EPOCH_MS;
      const seasonId = seasonIdText.trim() === '' ? undefined : Number(seasonIdText.trim());
      if (seasonId !== undefined && !Number.isInteger(seasonId)) {
        throw new Error('시즌 ID 는 정수여야 합니다');
      }
      const res = await adminScanDegenPrizeLog({
        fromMs,
        ...(kind !== ALL_KINDS ? { kind: kind as AdminDegenPrizeLogKind } : {}),
        ...(seasonId !== undefined ? { seasonId } : {}),
      });
      setResult(res);
      setLoadedPeriod(fetchedPeriod);
      setLoadedFilters({
        period: fetchedPeriod,
        ...(seasonId !== undefined ? { seasonId } : {}),
        kind,
      });
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  }

  const handleExportCsv = () => {
    if (!result) return;
    const rows = result.rows.map((r) => ({
      id: r.__id,
      kind: r.kind,
      seasonId: r.seasonId,
      account: r.account,
      sessionId: r.sessionId,
      amountGlory: r.amountGlory,
      ticketCount: r.ticketCount,
      difficulty: r.difficulty,
      timestamp: r.timestamp,
    }));
    const columns = [
      { key: 'id', header: 'id' },
      { key: 'kind', header: 'kind' },
      { key: 'seasonId', header: 'seasonId' },
      { key: 'account', header: 'account' },
      { key: 'sessionId', header: 'sessionId' },
      { key: 'amountGlory', header: 'amountGlory' },
      { key: 'ticketCount', header: 'ticketCount' },
      { key: 'difficulty', header: 'difficulty' },
      { key: 'timestamp', header: 'timestamp' },
    ];
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`degen-prize-log-${period}-${today}.csv`, rows, columns);
  };

  const loading = phase === 'loading';
  const needsReload = loadedPeriod != null && isWiderPeriod(period, loadedPeriod) && !loading;
  // 표는 최근순이 읽기 좋다 — 서버는 asc 로 주므로 뒤집어 상한만 뿌린다.
  const tableRows = result ? [...result.rows].reverse().slice(0, TABLE_ROW_LIMIT) : [];
  const state = result?.state ?? null;
  const sums = result?.sums ?? null;
  const reconcilable = isDegenPrizeReconcilable(loadedFilters);
  const accrualDiff =
    reconcilable && state && sums ? state.accruedGlory - sums.accrual.amountGlory : null;

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">디젠 상금 원장</h2>
        <p className="text-sm text-muted-foreground">
          degenPrizeLog(적립/미니/메가/구제 append-only 원장)를 기간·종류·시즌으로 조회합니다.
          합계·대사 비교는 서버가 계산하며 백페이 검증 도구를 겸합니다. verse 전환은 사이드바 Verse
          셀렉터를 이용하세요.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">조회 설정</CardTitle>
          <CardDescription>
            시즌 ID 를 넣으면 해당 시즌 원장 행만 조회합니다. 잭팟 잔액(degenPrizeState)은 시즌과
            무관한 전역 1행이라 필터와 무관하게 같은 값입니다 — 원장 합계와의 대사는 기간 '전체' ·
            시즌/종류 필터 없음일 때만 계산합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">기간</span>
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">종류</span>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_KINDS}>전체</SelectItem>
                {(Object.keys(KIND_LABEL) as AdminDegenPrizeLogKind[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABEL[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="degen-season-id">
              시즌 ID
            </label>
            <Input
              id="degen-season-id"
              aria-label="시즌 ID"
              className="w-28"
              placeholder="전체"
              value={seasonIdText}
              onChange={(e) => setSeasonIdText(e.target.value)}
            />
          </div>

          <Button type="button" onClick={handleLoad} disabled={loading}>
            {loading ? '불러오는 중…' : '원장 불러오기'}
          </Button>

          {phase === 'done' && result && result.rows.length > 0 && (
            <Button type="button" variant="outline" onClick={handleExportCsv}>
              CSV 다운로드 ({result.rows.length.toLocaleString()}건)
            </Button>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="whitespace-pre-wrap break-words">{error}</AlertDescription>
        </Alert>
      )}

      {phase === 'done' && result && !result.scan.complete && (
        <Alert variant="destructive">
          <AlertTitle>일부만 불러왔습니다</AlertTitle>
          <AlertDescription>
            스캔이 창을 완주하지 못했습니다 (truncated={String(result.scan.truncated)}, skippedRows=
            {result.scan.skippedRows}, malformed={result.scan.malformed}). 아래 합계는 과소집계일 수
            있습니다 — 기간을 좁혀 다시 불러오거나 lastTimestamp(
            {result.scan.lastTimestamp})부터 이어 조회하세요.
          </AlertDescription>
        </Alert>
      )}

      {needsReload && (
        <Alert>
          <AlertTitle>다시 불러와야 합니다</AlertTitle>
          <AlertDescription>
            현재 표는 직전에 불러온 기간 데이터라 과소집계입니다. "원장 불러오기" 를 다시 눌러
            주세요.
          </AlertDescription>
        </Alert>
      )}

      {phase === 'done' && sums && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {(Object.keys(KIND_LABEL) as AdminDegenPrizeLogKind[]).map((k) => (
            <Card key={k}>
              <CardHeader className="pb-2">
                <CardDescription>{KIND_LABEL[k]}</CardDescription>
                <CardTitle className="text-xl tabular-nums">
                  {sums[k].amountGlory.toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                {sums[k].count.toLocaleString()}건 (글로리 합계)
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase === 'done' && state && sums && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">잭팟 잔액 state 대사</CardTitle>
            <CardDescription>
              degenPrizeState 는 시즌과 무관한 전역 1행입니다 — 시즌 경계에서 리셋되지 않으므로
              시즌별 잔액이라는 것은 없습니다.{' '}
              {reconcilable
                ? '기간 전체 · 필터 없음 — 원장 합계와 직접 비교할 수 있습니다.'
                : '지금은 기간·시즌·종류 필터가 걸려 있어 원장 합계가 부분합입니다. 차이 계산은 생략합니다 — 기간을 ‘전체’ 로 두고 시즌·종류 필터를 비우면 대사가 켜집니다.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 text-sm lg:grid-cols-4">
            <div>
              풀 잔액: <span className="tabular-nums">{state.balanceGlory.toLocaleString()}</span>
            </div>
            <div>
              state 적립 누계:{' '}
              <span className="tabular-nums">{state.accruedGlory.toLocaleString()}</span>
            </div>
            <div>
              원장 적립 합계:{' '}
              <span className="tabular-nums">{sums.accrual.amountGlory.toLocaleString()}</span>
            </div>
            <div
              className={
                accrualDiff !== null && accrualDiff !== 0 ? 'font-semibold text-destructive' : ''
              }
            >
              적립 차이:{' '}
              <span className="tabular-nums">
                {accrualDiff === null ? '—' : accrualDiff.toLocaleString()}
              </span>
            </div>
            <div>
              state 메가 지급:{' '}
              <span className="tabular-nums">{state.paidMegaGlory.toLocaleString()}</span>
            </div>
            <div>
              원장 메가 합계:{' '}
              <span className="tabular-nums">{sums.mega.amountGlory.toLocaleString()}</span>
            </div>
            <div>
              state 미니 횟수: <span className="tabular-nums">{state.paidMiniCount}</span>
            </div>
            <div>
              원장 미니 건수: <span className="tabular-nums">{sums.mini.count}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {phase === 'done' && result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              원장 행 (매치 {result.totalMatched.toLocaleString()}건
              {result.rowsTruncated ? ' — 응답 상한으로 일부만 표시' : ''})
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>시각</TableHead>
                  <TableHead>종류</TableHead>
                  <TableHead>시즌</TableHead>
                  <TableHead>계정</TableHead>
                  <TableHead className="text-right">글로리</TableHead>
                  <TableHead className="text-right">티켓</TableHead>
                  <TableHead>난이도</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((r) => (
                  <TableRow key={r.__id}>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {new Date(r.timestamp).toISOString().replace('T', ' ').slice(0, 19)}
                    </TableCell>
                    <TableCell>{KIND_LABEL[r.kind]}</TableCell>
                    <TableCell className="tabular-nums">{r.seasonId}</TableCell>
                    <TableCell className="font-mono text-xs">{r.account}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.amountGlory.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{r.ticketCount}</TableCell>
                    <TableCell>{r.difficulty}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
